import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Users, Clock, Radio, Play, Video,
  Share2, Volume2, VolumeX, Maximize,
  Bookmark, Scissors, MapPin, UserPlus, UserMinus, Minimize,
  Lock, Flag, ListChecks, Ticket, Loader2, X, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { RooamVideoPlayer } from '../components/RooamVideoPlayer';
import { WatermarkOverlay } from '../components/WatermarkOverlay';
import { VODReplayBadge } from '../components/VODReplayBadge';
import { BoxScoreStats } from '../components/BoxScoreStats';
import { PlayByPlayFeed } from '../components/PlayByPlayFeed';
import { GamePlayerStats } from '../components/GamePlayerStats';
import ClipCreator from '../components/ClipCreator';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GameWatchLive() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [game, setGame] = useState(null);
  const [streamSession, setStreamSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(true);
  const [previewExpired, setPreviewExpired] = useState(false);
  const [previewSeconds, setPreviewSeconds] = useState(60);
  const previewTimerRef = useRef(null);
  const [isSaved, setIsSaved] = useState(false);
  const [followedTeams, setFollowedTeams] = useState(new Set());

  // Check stored preview budget on mount
  useEffect(() => {
    if (isAuthenticated()) return;
    const used = parseInt(localStorage.getItem('rooam_preview_used') || '0', 10);
    const remaining = Math.max(60 - used, 0);
    setPreviewSeconds(remaining);
    if (remaining <= 0) {
      setPreviewExpired(true);
    }
  }, [isAuthenticated]);

  // Check if preview gate is bypassed (global toggle, per-game free preview, or event pass)
  const [gateBypass, setGateBypass] = useState(false);
  const [requiresPass, setRequiresPass] = useState(false);
  const [passEventId, setPassEventId] = useState(null);
  useEffect(() => {
    const checkGate = async () => {
      try {
        const [gateRes, gameRes] = await Promise.all([
          fetch(`${API_URL}/api/preview-gate/status`),
          fetch(`${API_URL}/api/engine/games/${gameId}`)
        ]);
        const gateData = await gateRes.json();
        const gameData = await gameRes.json();
        if (gameData.event_id) setPassEventId(gameData.event_id);
        // Bypass if global gate is disabled OR this specific game is marked free
        if (!gateData.enabled || gameData.free_preview) {
          setGateBypass(true);
          setPreviewExpired(false);
          return;
        }
        // If authenticated, check game-level access (handles event passes + day validation)
        if (isAuthenticated() && token) {
          try {
            const accessRes = await fetch(`${API_URL}/api/games/${gameId}/access`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (accessRes.ok) {
              const accessData = await accessRes.json();
              if (accessData.has_access) {
                setGateBypass(true);
                setPreviewExpired(false);
              }
              if (accessData.requires_pass) {
                setRequiresPass(true);
                if (accessData.event_id) setPassEventId(accessData.event_id);
              }
            }
          } catch { /* ignore */ }
        } else if (gameData.event_id) {
          // For unauthenticated users, check if this event has pass tiers
          try {
            const tierRes = await fetch(`${API_URL}/api/events/${gameData.event_id}/passes`);
            if (tierRes.ok) {
              const tierData = await tierRes.json();
              if (tierData.total > 0) setRequiresPass(true);
            }
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    };
    checkGate();
  }, [gameId, token]);
  const [overlaySettings, setOverlaySettings] = useState({
    showWatermark: false,  // Default OFF — operators can enable per-game from the Operator Console
    watermarkPosition: 'top-right',
    watermarkOpacity: 0.6
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [replayEnded, setReplayEnded] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [timestampMarkers, setTimestampMarkers] = useState([]);
  const [showMarkersList, setShowMarkersList] = useState(false);
  const [gameClips, setGameClips] = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);

  // Lazy-fetch clips when the user opens the Clips tab (avoids extra load on default view)
  useEffect(() => {
    if (activeTab !== 'clips' || !gameId) return;
    let cancelled = false;
    (async () => {
      setClipsLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/vod/clips?game_id=${gameId}`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setGameClips(Array.isArray(data) ? data : (data.items || []));
        }
      } catch { /* ignore */ }
      if (!cancelled) setClipsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [activeTab, gameId]);
  
  // Report Issue state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState('');
  const [reportNote, setReportNote] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const handleReportSubmit = async () => {
    if (!reportType) { toast.error('Please select an issue type'); return; }
    setReportSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/stream-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          game_id: gameId,
          game_title: game?.title || `${game?.home_team_name} vs ${game?.away_team_name}`,
          event_id: game?.event_id || null,
          issue_type: reportType,
          note: reportNote,
        }),
      });
      if (res.ok) {
        toast.success('Report submitted — our team has been notified');
        setShowReportModal(false);
        setReportType('');
        setReportNote('');
      } else {
        toast.error('Failed to submit report');
      }
    } catch {
      toast.error('Failed to submit report');
    } finally {
      setReportSubmitting(false);
    }
  };
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const wsRef = useRef(null);

  // Fetch timestamp markers for this game
  const fetchTimestampMarkers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/user/timestamps/${gameId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const markers = await res.json();
        setTimestampMarkers(markers);
      }
    } catch (err) {
      console.debug('Failed to fetch timestamp markers:', err);
    }
  }, [token, gameId]);

  // Mark current timestamp for later clipping
  const markTimestamp = async () => {
    if (!token) {
      toast.error('Sign in to mark timestamps');
      return;
    }
    
    // Get current video time
    let currentTime = 0;
    if (videoRef.current) {
      currentTime = videoRef.current.currentTime || 0;
    }
    
    // Also calculate approximate game time based on clock if available
    const clockTime = game?.clock?.time_remaining_ms 
      ? Math.floor((game.clock.period_length_ms - game.clock.time_remaining_ms) / 1000)
      : currentTime;
    
    try {
      const res = await fetch(`${API_URL}/api/user/timestamps`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          game_id: gameId,
          timestamp_seconds: clockTime,
          label: `${game?.home_team_name} vs ${game?.away_team_name} - ${game?.score?.home || 0}-${game?.score?.away || 0}`,
          note: `Period ${game?.clock?.period || 1}`
        })
      });
      
      if (res.ok) {
        toast.success('Timestamp marked! Create a clip from this moment after the game ends.');
        fetchTimestampMarkers();
      } else {
        toast.error('Failed to mark timestamp');
      }
    } catch (err) {
      console.error('Failed to mark timestamp:', err);
      toast.error('Failed to mark timestamp');
    }
  };

  // Delete a timestamp marker
  const deleteMarker = async (markerId) => {
    if (!token) return;
    try {
      await fetch(`${API_URL}/api/user/timestamps/${markerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setTimestampMarkers(prev => prev.filter(m => m.id !== markerId));
      toast.success('Marker deleted');
    } catch (err) {
      console.error('Failed to delete marker:', err);
      toast.error('Failed to delete marker');
    }
  };

  // Handle fullscreen changes - cross-browser compatible
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isInFullscreen = document.fullscreenElement || 
                             document.webkitFullscreenElement || 
                             document.mozFullScreenElement ||
                             document.msFullscreenElement;
      setIsFullscreen(!!isInFullscreen);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // 60-second preview timer (global budget across all games)
  // Runs for: unauthenticated users, OR authenticated users on pass-required games without access
  useEffect(() => {
    const shouldRunTimer = !gateBypass && (
      !isAuthenticated() || (isAuthenticated() && requiresPass)
    );
    if (!shouldRunTimer || previewExpired) return;
    previewTimerRef.current = setInterval(() => {
      setPreviewSeconds((prev) => {
        const next = prev - 1;
        const totalUsed = 60 - next;
        localStorage.setItem('rooam_preview_used', String(totalUsed));
        if (next <= 0) {
          clearInterval(previewTimerRef.current);
          setPreviewExpired(true);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(previewTimerRef.current);
  }, [isAuthenticated, previewExpired, gateBypass, requiresPass]);

  // Toggle fullscreen for video container - cross-browser compatible
  // Helper to detect mobile device
  const isMobileDevice = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const elem = videoContainerRef.current;
    if (!elem) return;
    
    // Check if we're in fullscreen
    const isInFullscreen = document.fullscreenElement || 
                           document.webkitFullscreenElement || 
                           document.mozFullScreenElement ||
                           document.msFullscreenElement;
    
    if (!isInFullscreen) {
      // Enter fullscreen - try different browser methods
      const enterFullscreen = async () => {
        try {
          if (elem.requestFullscreen) {
            await elem.requestFullscreen();
          } else if (elem.webkitRequestFullscreen) {
            elem.webkitRequestFullscreen();
          } else if (elem.mozRequestFullScreen) {
            elem.mozRequestFullScreen();
          } else if (elem.msRequestFullscreen) {
            elem.msRequestFullscreen();
          } else if (videoRef.current?.webkitEnterFullscreen) {
            // iOS Safari fallback - fullscreen the video directly
            videoRef.current.webkitEnterFullscreen();
          } else {
            toast.error('Fullscreen not supported on this device');
            return;
          }

          // Auto-rotate to landscape on mobile devices
          if (isMobileDevice() && screen.orientation && screen.orientation.lock) {
            try {
              await screen.orientation.lock('landscape');
            } catch (e) {
              // Orientation lock not supported or denied - that's okay
              console.log('Orientation lock not available:', e.message);
            }
          }
        } catch (err) {
          console.error('Fullscreen error:', err);
          // Fallback: try on the video element directly for iOS
          if (videoRef.current?.webkitEnterFullscreen) {
            videoRef.current.webkitEnterFullscreen();
          } else {
            toast.error('Fullscreen not supported on this device');
          }
        }
      };
      
      enterFullscreen();
    } else {
      // Exit fullscreen
      const exitFullscreen = async () => {
        try {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }

          // Unlock orientation when exiting fullscreen on mobile
          if (isMobileDevice() && screen.orientation && screen.orientation.unlock) {
            try {
              screen.orientation.unlock();
            } catch (e) {
              // Orientation unlock not supported - that's okay
              console.log('Orientation unlock not available:', e.message);
            }
          }
        } catch (err) {
          console.error('Exit fullscreen error:', err);
        }
      };
      
      exitFullscreen();
    }
  }, [isMobileDevice]);

  useEffect(() => {
    fetchGame();
    connectWebSocket();
    fetchTimestampMarkers();

    // Log watch history and check saved status
    if (token) {
      fetch(`${API_URL}/api/user/watch-history/${gameId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch((err) => console.debug('Watch history log failed:', err));
      fetch(`${API_URL}/api/user/saved-games/ids`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : []).then(ids => {
        if (ids.includes(gameId)) setIsSaved(true);
      }).catch((err) => console.debug('Saved games check failed:', err));
      fetch(`${API_URL}/api/user/followed-teams/names`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : []).then(names => {
        setFollowedTeams(new Set(names));
      }).catch((err) => console.debug('Followed teams check failed:', err));
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [gameId]);

  const fetchGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setGame(data);
        
        // Load overlay settings from game if available
        if (data.overlay_settings) {
          setOverlaySettings(prev => ({
            ...prev,
            ...data.overlay_settings
          }));
        }
        
        // Fetch stream session if available
        let session = null;
        if (data.stream_session_id) {
          const sessionRes = await fetch(`${API_URL}/api/engine/stream/sessions/by-id/${data.stream_session_id}`);
          if (sessionRes.ok) {
            const sessionData = await sessionRes.json();
            session = sessionData.session;
          }
        }
        if (!session) {
          const sessionRes = await fetch(`${API_URL}/api/engine/stream/sessions/${data.game_id || gameId}`);
          if (sessionRes.ok) {
            const sessionData = await sessionRes.json();
            session = sessionData.session;
          }
        }
        
        // ALWAYS try the live-playback endpoint — it searches court streams, 
        // stream sessions, and Cloudflare API as fallbacks
        try {
          const liveRes = await fetch(`${API_URL}/api/engine/stream/${data.game_id || gameId}/live-playback`);
          if (liveRes.ok) {
            const liveData = await liveRes.json();
            if (liveData.playback_url) {
              if (!session) {
                // Create a synthetic session so the player renders
                session = {
                  session_id: data.stream_session_id || 'court-stream',
                  game_id: data.game_id || gameId,
                  platform: 'cloudflare',
                  playback_url: liveData.playback_url,
                  hls_url: liveData.playback_url,
                  status: 'active',
                  is_custom: true,
                };
              } else {
                session.playback_url = liveData.playback_url;
                session.hls_url = liveData.playback_url;
              }
            }
          }
        } catch (e) {
          console.log('Live playback fetch failed:', e);
        }
        
        // Fallback: if game has court info, try fetching court stream directly
        if (!session && data.event_id) {
          try {
            const courtsRes = await fetch(`${API_URL}/api/court-streams/event/${data.event_id}`);
            if (courtsRes.ok) {
              const courts = await courtsRes.json();
              // Find court with this game active or matching court name
              const matchCourt = courts.find(c => 
                c.current_game_id === gameId || 
                c.court_name === data.court_name
              );
              if (matchCourt && matchCourt.playback_url) {
                session = {
                  session_id: matchCourt.court_stream_id,
                  game_id: gameId,
                  platform: matchCourt.platform || 'custom',
                  playback_url: matchCourt.playback_url,
                  hls_url: matchCourt.playback_url,
                  status: 'active',
                  is_custom: true,
                  custom_rtmp_url: matchCourt.rtmps_url,
                  custom_stream_key: matchCourt.rtmps_stream_key,
                };
              }
            }
          } catch (e) {
            console.log('Court stream fallback failed:', e);
          }
        }

        if (session) {
          setStreamSession(session);
        }

        // FIX VOD URLs: For finalized games, check if video_url is a live-input URL
        // (which shows the current live stream instead of the recording).
        // If so, find the correct recording from Cloudflare.
        const isFinal = data.status === 'final' || data.status === 'ended' || data.status === 'completed';
        if (isFinal && data.video_url && data.video_url.includes('cloudflarestream.com')) {
          // Extract UID from video_url
          const urlParts = data.video_url.split('cloudflarestream.com/');
          if (urlParts.length > 1) {
            const urlUid = urlParts[1].split('/')[0];
            // Check if this UID matches any court's live input (meaning it's wrong)
            try {
              const eventId = data.event_id;
              if (eventId) {
                const courtsRes = await fetch(`${API_URL}/api/court-streams/event/${eventId}`);
                if (courtsRes.ok) {
                  const courts = await courtsRes.json();
                  const isLiveInputUrl = courts.some(c => {
                    const courtPb = c.playback_url || '';
                    if (courtPb.includes('cloudflarestream.com/')) {
                      const courtUid = courtPb.split('cloudflarestream.com/')[1].split('/')[0];
                      return courtUid === urlUid;
                    }
                    return false;
                  });
                  
                  if (isLiveInputUrl) {
                    // This video_url points to a live input — fetch the actual recording
                    try {
                      const recRes = await fetch(`${API_URL}/api/cloudflare/live_inputs/${urlUid}/recordings`);
                      if (recRes.ok) {
                        const recData = await recRes.json();
                        const recordings = recData.recordings || [];
                        // Find a ready recording (not live-inprogress)
                        const readyRec = recordings.find(r => r.ready_to_stream || r.status === 'ready');
                        if (readyRec && readyRec.playback_url) {
                          // Override the game's video_url with the correct recording URL
                          setGame(prev => ({ ...prev, video_url: readyRec.playback_url }));
                        }
                      }
                    } catch (e) {
                      console.log('Recording fetch failed:', e);
                    }
                  }
                }
              }
            } catch (e) {
              console.log('VOD URL fix check failed:', e);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching game:', err);
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    // Determine WebSocket URL based on API URL
    let wsUrl;
    if (API_URL.startsWith('https://')) {
      wsUrl = `wss://${API_URL.replace('https://', '')}/api/engine/ws/game/${gameId}`;
    } else {
      wsUrl = `ws://${API_URL.replace('http://', '')}/api/engine/ws/game/${gameId}`;
    }

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        setTimeout(connectWebSocket, 3000);
      };
      ws.onerror = () => ws.close();

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          // Handle various update types
          if (message.type === 'game_update' || message.type === 'clock_update' || message.type === 'score_update') {
            setGame(prev => ({ ...prev, ...message }));
          }
          // Handle status changes (e.g., operator pressing Final)
          if (message.type === 'status' && message.status) {
            setGame(prev => ({ ...prev, status: message.status }));
          }
          // Handle stat updates - update score
          if (message.type === 'stat' && message.score) {
            setGame(prev => ({ ...prev, score: message.score }));
          }
          // Handle undo updates
          if (message.type === 'undo' && message.score) {
            setGame(prev => ({ ...prev, score: message.score }));
          }
          // Handle timeout updates
          if (message.type === 'timeout') {
            setGame(prev => ({ 
              ...prev, 
              clock: message.clock,
              [`${message.team_side}_timeouts_remaining`]: message.timeouts_remaining
            }));
          }
          // Handle manual score adjustments
          if (message.type === 'score_adjust' && message.score) {
            setGame(prev => ({ ...prev, score: message.score }));
          }
          // Handle overlay settings updates from operator
          if (message.type === 'OVERLAY_SETTINGS_UPDATED' && message.settings) {
            setOverlaySettings(prev => ({ ...prev, ...message.settings }));
          }
        } catch (e) {
          console.error('WebSocket parse error:', e);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  };

  const formatTime = (ms) => {
    if (!ms && ms !== 0) return '--:--';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // Determine video source with priority: HLS > Direct > YouTube (fallback)
  // IMPORTANT: game.video_url (replay/VOD) is ONLY used when game is finalized.
  // During live/pre-game, only the stream session's live playback URL is used.
  const getVideoSource = useCallback(() => {
    if (!game) return { type: null, src: null };

    const isLive = game.status === 'live' || game.status === 'halftime' || game.status === 'warmup';
    const isFinalized = game.status === 'final' || game.status === 'ended' || game.status === 'completed' || game.status === 'replay';

    // Helper to extract YouTube video ID
    const extractYouTubeId = (url) => {
      if (!url) return null;
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
        /youtube\.com\/live\/([^&\s?]+)/
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
      return null;
    };

    // === LIVE GAME: only use the stream session's live playback URL ===
    if (isLive) {
      if (streamSession?.playback_url?.includes('.m3u8')) {
        return { type: 'hls', src: streamSession.playback_url };
      }
      if (streamSession?.playback_url) {
        return { type: 'direct', src: streamSession.playback_url };
      }
      if (streamSession?.platform === 'youtube' && streamSession?.youtube_video_id) {
        return { type: 'youtube', src: streamSession.youtube_video_id };
      }
      if (streamSession?.platform === 'twitch' && streamSession?.custom_stream_key) {
        return { type: 'twitch', src: streamSession.custom_stream_key, isVideo: false };
      }
      return { type: null, src: null };
    }

    // === FINALIZED GAME: allow replay/VOD sources ===
    if (isFinalized) {
      // PRIORITY 1: game.video_url - this is the VOD set by admin
      if (game.video_url) {
        // Normalize Cloudflare URLs - convert /watch to /manifest/video.m3u8
        let videoUrl = game.video_url;
        if (videoUrl.includes('cloudflarestream.com') && videoUrl.endsWith('/watch')) {
          videoUrl = videoUrl.replace('/watch', '/manifest/video.m3u8');
        }
        
        // Check if it's an HLS stream (.m3u8)
        if (videoUrl.includes('.m3u8')) {
          return { type: 'hls', src: videoUrl };
        }
        // Check if it's YouTube
        const youtubeId = extractYouTubeId(videoUrl);
        if (youtubeId) return { type: 'youtube', src: youtubeId };
        // Check if it's Twitch
        if (videoUrl.includes('twitch.tv')) {
          const twitchMatch = videoUrl.match(/twitch\.tv\/(?:videos\/)?(\w+)/);
          if (twitchMatch) return { type: 'twitch', src: twitchMatch[1], isVideo: videoUrl.includes('/videos/') };
        }
        // Direct video (MP4, WebM, etc)
        const finalUrl = videoUrl.startsWith('/') ? `${API_URL}${videoUrl}` : videoUrl;
        return { type: 'direct', src: finalUrl };
      }
      
      // PRIORITY 2: Stream session playback URL (fallback)
      if (streamSession?.playback_url?.includes('.m3u8')) {
        return { type: 'hls', src: streamSession.playback_url };
      }
      if (streamSession?.playback_url) {
        return { type: 'direct', src: streamSession.playback_url };
      }
      if (streamSession?.platform === 'youtube' && streamSession?.youtube_video_id) {
        return { type: 'youtube', src: streamSession.youtube_video_id };
      }
    }

    // === PRE-GAME / SCHEDULED: no video source ===
    return { type: null, src: null };
  }, [game, streamSession]);

  const renderVideoPlayer = () => {
    if (!game) return null;

    const isLiveGame = game.status === 'live' || game.status === 'halftime' || game.status === 'warmup';
    const isVOD = game.status === 'final' || game.status === 'ended' || game.status === 'completed' || game.status === 'replay';
    const isPreGame = game.status === 'scheduled' || game.status === 'upcoming';
    const videoSource = getVideoSource();

    // Use custom Rooam player for HLS and direct video
    if (videoSource.type === 'hls' || videoSource.type === 'direct') {
      return (
        <RooamVideoPlayer
          ref={videoRef}
          src={videoSource.src}
          isLive={isLiveGame}
          isVOD={isVOD}
          autoPlay={true}
          muted={muted}
          className="w-full h-full"
          showControls={true}
          onEnded={() => { if (isVOD) setReplayEnded(true); }}
        >
          {/* Overlays rendered inside custom player */}
          {/* Corner scorebug removed — the top scorebar built into the player already shows the score. */}
          {overlaySettings.showWatermark && (
            <WatermarkOverlay 
              position={overlaySettings.watermarkPosition}
              opacity={overlaySettings.watermarkOpacity}
            />
          )}
        </RooamVideoPlayer>
      );
    }

    // YouTube fallback (keeps existing YouTube integration)
    if (videoSource.type === 'youtube') {
      const origin = encodeURIComponent(window.location.origin);
      const embedUrl = `https://www.youtube.com/embed/${videoSource.src}?autoplay=1&mute=${muted ? 1 : 0}&rel=0&modestbranding=1&playsinline=1&origin=${origin}&enablejsapi=1`;
      return (
        <iframe
          className="w-full h-full"
          src={embedUrl}
          title="YouTube Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      );
    }

    // Twitch fallback
    if (videoSource.type === 'twitch') {
      const embedUrl = videoSource.isVideo 
        ? `https://player.twitch.tv/?video=${videoSource.src}&parent=${window.location.hostname}&muted=${muted}`
        : `https://player.twitch.tv/?channel=${videoSource.src}&parent=${window.location.hostname}&muted=${muted}`;
      return (
        <iframe
          className="w-full h-full"
          src={embedUrl}
          title="Twitch Video"
          frameBorder="0"
          allowFullScreen
        />
      );
    }

    // Custom RTMP configured but no playback URL yet
    if (streamSession?.is_custom && (streamSession?.custom_rtmp_url || streamSession?.custom_stream_key)) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-900 to-black p-4">
          <div className="text-center max-w-lg">
            {/* Rooam Logo - LARGE and Center of Attention */}
            <div className="mb-8">
              <img 
                src="/rooam-logo.png" 
                alt="Rooam Sports" 
                className="h-32 sm:h-40 lg:h-48 mx-auto drop-shadow-2xl"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <p className="text-white text-lg font-medium">
                {isLiveGame ? 'Stream is LIVE' : 'Waiting for Broadcast'}
              </p>
            </div>
            <p className="text-zinc-500 text-sm">
              {isLiveGame 
                ? 'Video feed loading...'
                : 'The stream will appear once the broadcaster goes live.'}
            </p>
          </div>
        </div>
      );
    }

    // No stream configured - show appropriate state based on game status
    const isGameFinished = game.status === 'final' || game.status === 'completed';
    const isGameScheduled = game.status === 'scheduled' || game.status === 'pregame';
    
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-900 to-black p-4">
        <div className="text-center max-w-md">
          {/* Rooam Logo */}
          <div className="mb-4">
            <img 
              src="/rooam-logo.png" 
              alt="Rooam Sports" 
              className="h-16 sm:h-20 lg:h-24 mx-auto drop-shadow-2xl"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          {game?.notice ? (
            <>
              <div className="flex items-center justify-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <p className="text-amber-400 text-base font-semibold">Notice</p>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">{game.notice}</p>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center gap-3 mb-2">
                {isLiveGame && <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />}
                <p className="text-white text-base font-medium">
                  {isLiveGame 
                    ? 'Game is LIVE' 
                    : isGameFinished 
                      ? 'Replay Unavailable' 
                      : 'Stream Not Started'}
                </p>
              </div>
              <p className="text-zinc-500 text-sm">
                {isLiveGame 
                  ? 'Follow the live score below!'
                  : isGameFinished
                    ? 'This replay is no longer available.'
                    : isGameScheduled
                      ? 'Come back when the game begins!'
                      : 'The broadcast will begin shortly.'}
              </p>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Game not found</p>
          <Link to="/browse">
            <Button variant="outline">Back to Browse</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isLive = game.status === 'live' || game.status === 'halftime' || game.status === 'warmup';

  return (
    <div className="min-h-screen bg-zinc-950 text-white overflow-x-hidden" data-testid="game-watch-live">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-3 sm:px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Link to={game.event_id ? `/event/${game.event_id}` : '/events'} className="p-2 hover:bg-zinc-800 rounded-lg flex-shrink-0" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-semibold text-sm sm:text-base truncate">{game.title || `${game.home_team_name} vs ${game.away_team_name}`}</h1>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-zinc-400">
                {game.location && <span className="truncate">{game.location}</span>}
                {game.sport && (
                  <Badge className="bg-rooam-red/20 text-rooam-red border-rooam-red/30 capitalize text-xs flex-shrink-0">
                    {game.sport}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {isLive && (
              <Badge className="bg-red-600 text-white animate-pulse text-xs">
                <div className="w-2 h-2 bg-white rounded-full mr-1" />
                LIVE
              </Badge>
            )}
            {game.status === 'final' && (
              <Badge className="bg-zinc-700 text-zinc-300 text-xs">REPLAY</Badge>
            )}
            <Button variant="outline" size="sm" onClick={handleShare} className="hidden sm:flex">
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
            <Button variant="outline" size="icon" onClick={handleShare} className="sm:hidden h-8 w-8">
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Video Player */}
          <div className="lg:col-span-2 order-1">
            <div 
              ref={videoContainerRef}
              className={`relative bg-black rounded-xl overflow-hidden ${isFullscreen ? 'fixed inset-0 z-50 rounded-none' : 'aspect-video'}`}
            >
              {renderVideoPlayer()}

              {/* BallerTV-style Score Overlay on Video */}
              {game && !isFullscreen && (
                <div className="absolute top-0 left-0 right-0 z-20 pointer-events-none">
                  <div className="flex items-center justify-center py-2 px-3 bg-gradient-to-b from-black/60 to-transparent">
                    <div className="flex items-center gap-2 text-white text-xs sm:text-sm font-semibold">
                      <span className="truncate max-w-[60px] sm:max-w-none">{(game.home_team_name || 'Home').substring(0, 3).toUpperCase()}</span>
                      <span className="tabular-nums font-bold">{game.score?.home || 0}</span>
                      <span className="text-zinc-400 mx-1">|</span>
                      <span className="text-zinc-300 text-[10px] sm:text-xs">
                        {game.status === 'live' || game.status === 'halftime' 
                          ? `${game.period || 'Q1'}` 
                          : game.status === 'final' 
                            ? 'FINAL' 
                            : game.period || '--'}
                      </span>
                      <span className="text-zinc-400 mx-1">|</span>
                      <span className="tabular-nums font-bold">{game.score?.away || 0}</span>
                      <span className="truncate max-w-[60px] sm:max-w-none">{(game.away_team_name || 'Away').substring(0, 3).toUpperCase()}</span>
                    </div>
                    {/* Removed redundant fullscreen button - using single button in controls */}
                  </div>
                </div>
              )}

              {/* Preview gate - shows after 60s preview expires */}
              {/* For unauthenticated users: always show after preview */}
              {/* For authenticated users: show if they lack access on pass-required games */}
              {!gateBypass && (
                (() => {
                  const showGate = !isAuthenticated()
                    ? previewExpired
                    : (requiresPass && previewExpired);
                  const showTimer = !isAuthenticated() && !previewExpired;

                  if (showGate) {
                    return (
                      <div className="absolute inset-0 z-40 bg-zinc-950/95 backdrop-blur-sm flex items-center justify-center" data-testid="preview-gate">
                        <div className="text-center px-6 max-w-md">
                          <div className="w-16 h-16 rounded-full bg-[#FF453A]/10 flex items-center justify-center mx-auto mb-5">
                            {requiresPass 
                              ? <Ticket className="h-8 w-8 text-[#FF453A]" />
                              : <Lock className="h-8 w-8 text-rooam-blue" />
                            }
                          </div>
                          <h2 className="text-2xl font-bold text-white mb-2">
                            {requiresPass ? 'Event Pass Required' : 'Preview Ended'}
                          </h2>
                          <p className="text-gray-400 text-sm mb-6">
                            {requiresPass
                              ? 'Purchase an event pass to watch this game. Passes are sold separately from memberships.'
                              : 'Sign up to watch the full game, access replays, and track your favorite athletes.'
                            }
                          </p>
                          <div className="flex flex-col gap-3">
                            {/* Event Pass is PRIMARY for pass-required games */}
                            {requiresPass && passEventId && (
                              <Button
                                className="w-full bg-[#FF453A] hover:bg-[#FF5A50] text-white font-semibold"
                                onClick={() => navigate(`/event/${passEventId}`)}
                                data-testid="preview-event-pass-btn"
                              >
                                <Ticket className="h-4 w-4 mr-2" />
                                Buy Event Pass
                              </Button>
                            )}
                            {/* Membership is secondary for pass-required, primary for regular */}
                            {!isAuthenticated() && (
                              <Button
                                className={requiresPass
                                  ? "w-full border-zinc-600 text-gray-300 hover:bg-zinc-800"
                                  : "w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold"
                                }
                                variant={requiresPass ? "outline" : "default"}
                                onClick={() => navigate('/membership')}
                                data-testid="preview-signup-btn"
                              >
                                <UserPlus className="h-4 w-4 mr-2" />
                                {requiresPass ? 'Or Get a Membership' : 'View Membership Plans'}
                              </Button>
                            )}
                            {/* Non-pass event: no event pass button */}
                            {!isAuthenticated() && (
                              <Button
                                variant="outline"
                                className="w-full border-zinc-600 text-gray-300 hover:bg-zinc-800"
                                onClick={() => navigate('/login')}
                                data-testid="preview-login-btn"
                              >
                                Already have an account? Sign In
                              </Button>
                            )}
                          </div>
                          {requiresPass && (
                            <p className="text-xs text-zinc-600 mt-4">Event passes are separate from memberships.</p>
                          )}
                        </div>
                      </div>
                    );
                  }

                  if (showTimer) {
                    return (
                      <div className="absolute top-3 right-3 z-30" data-testid="preview-timer">
                        <Badge className="bg-zinc-900/80 text-white border-zinc-700 backdrop-blur-sm font-mono text-xs px-2 py-1">
                          <Clock className="h-3 w-3 mr-1.5" />
                          Preview: {previewSeconds}s
                        </Badge>
                      </div>
                    );
                  }

                  return null;
                })()
              )}
              
              {/* Overlays for YouTube/Twitch embeds only (custom player handles its own) */}
              {(() => {
                const videoSource = getVideoSource();
                const isEmbed = videoSource.type === 'youtube' || videoSource.type === 'twitch';
                const isLiveGame = game?.status === 'live' || game?.status === 'halftime' || game?.status === 'warmup';
                const isVODGame = game?.status === 'final' || game?.status === 'ended' || game?.status === 'completed' || game?.status === 'replay';
                
                if (!isEmbed || !game) return null;
                
                return (
                  <>
                    {/* Corner scorebug removed — top scorebar shows score */}
                    {overlaySettings.showWatermark && (
                      <WatermarkOverlay 
                        position={overlaySettings.watermarkPosition}
                        opacity={overlaySettings.watermarkOpacity}
                      />
                    )}
                  </>
                );
              })()}
              
              {/* Video Controls Overlay - for YouTube/Twitch embeds only */}
              {(() => {
                const videoSource = getVideoSource();
                const isEmbed = videoSource.type === 'youtube' || videoSource.type === 'twitch';
                if (!isEmbed) return null;
                
                return (
                  <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 ${isFullscreen ? 'p-6' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {isLive && (
                          <Badge className="bg-red-600 text-white">
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse mr-1" />
                            LIVE
                          </Badge>
                        )}
                        {game.status === 'final' && (
                          <Badge className="bg-zinc-600">FINAL</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setMuted(!muted)}
                          className="text-white hover:bg-white/20"
                        >
                          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={toggleFullscreen}
                          className="text-white hover:bg-white/20"
                          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        >
                          {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Replay End Screen */}
              {replayEnded && game && (
                <div className="absolute inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="replay-end-screen">
                  <div className="text-center w-full max-w-sm">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-rooam-blue font-semibold mb-3">Final Score</p>
                    
                    {/* Compact score display */}
                    <div className="flex items-center justify-center gap-3 mb-1">
                      <div className="text-center min-w-0">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm mx-auto mb-1"
                          style={{ backgroundColor: game.home_team_color || '#3B82F6' }}
                        >
                          {game.home_team_name?.charAt(0) || 'H'}
                        </div>
                        <p className="text-white text-[11px] font-semibold">{(game.home_team_name || '').replace(/^(The |Team )/i, '').substring(0, 3).toUpperCase()}</p>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-black text-white tabular-nums">{game.score?.home || 0}</span>
                        <span className="text-sm text-zinc-600">-</span>
                        <span className="text-3xl font-black text-white tabular-nums">{game.score?.away || 0}</span>
                      </div>
                      <div className="text-center min-w-0">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm mx-auto mb-1"
                          style={{ backgroundColor: game.away_team_color || '#EF4444' }}
                        >
                          {game.away_team_name?.charAt(0) || 'A'}
                        </div>
                        <p className="text-white text-[11px] font-semibold">{(game.away_team_name || '').replace(/^(The |Team )/i, '').substring(0, 3).toUpperCase()}</p>
                      </div>
                    </div>

                    <p className="text-base font-bold text-white mt-4 mb-0.5">Thank You for Watching</p>
                    <p className="text-zinc-500 text-xs mb-4">Powered by Rooam Sports Network</p>

                    {/* Action buttons - compact */}
                    <div className="flex flex-col gap-2 max-w-[260px] mx-auto">
                      <button
                        onClick={() => { setReplayEnded(false); }}
                        className="w-full flex items-center justify-center gap-2 bg-rooam-blue hover:bg-rooam-blue-light text-black font-bold text-xs py-2 px-3 rounded-lg transition-colors"
                        data-testid="replay-again-btn"
                      >
                        <Play className="h-3.5 w-3.5" /> Watch Again
                      </button>
                      <button
                        onClick={() => { setReplayEnded(false); document.getElementById('box-score-section')?.scrollIntoView({ behavior: 'smooth' }); }}
                        className="w-full flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs py-2 px-3 rounded-lg border border-zinc-700 transition-colors"
                        data-testid="view-boxscore-btn"
                      >
                        <Users className="h-3.5 w-3.5" /> Box Score
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate('/events')}
                          className="flex-1 flex items-center justify-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs py-2 px-2 rounded-lg border border-zinc-700 transition-colors"
                          data-testid="browse-games-btn"
                        >
                          <Scissors className="h-3.5 w-3.5" /> More Games
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard?.writeText(window.location.href);
                            toast.success('Game link copied!');
                          }}
                          className="flex-1 flex items-center justify-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs py-2 px-2 rounded-lg border border-zinc-700 transition-colors"
                          data-testid="share-game-btn"
                        >
                          <Share2 className="h-3.5 w-3.5" /> Share
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Compact Action Toolbar - BallerTV Style */}
            {!isFullscreen && (
              <div className="flex items-center justify-between mt-3 px-1 gap-2">
                {/* Left: Primary Actions */}
                <div className="flex items-center gap-2">
                  {/* LIVE: Mark Timestamp button | VOD: Clip Creator button */}
                  {(game.status === 'live' || game.status === 'halftime' || game.status === 'warmup') ? (
                    // LIVE GAME: Show Mark Timestamp button
                    <>
                      <Button 
                        onClick={markTimestamp}
                        className="bg-amber-500 hover:bg-amber-400 text-black font-semibold h-9 px-4 rounded-lg"
                        data-testid="mark-timestamp-btn"
                      >
                        <Flag className="w-4 h-4 mr-1.5" />
                        Mark
                      </Button>
                      {timestampMarkers.length > 0 && (
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => setShowMarkersList(!showMarkersList)}
                          className="border-amber-600 text-amber-400 hover:bg-amber-600/20 h-9 px-3 rounded-lg"
                          data-testid="view-markers-btn"
                        >
                          <ListChecks className="w-4 h-4 mr-1" />
                          {timestampMarkers.length}
                        </Button>
                      )}
                    </>
                  ) : (game.status === 'final' || game.status === 'ended' || game.status === 'completed' || game.status === 'replay') ? (
                    // VOD/REPLAY ONLY: Show Clip Creator button
                    <ClipCreator
                      gameId={gameId}
                      videoRef={videoRef}
                      gameName={`${game.home_team_name} vs ${game.away_team_name}`}
                      compact={false}
                      yellowStyle={true}
                      timestampMarkers={timestampMarkers}
                    />
                  ) : null}
                  
                  {/* Share button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-800 h-9 px-3 rounded-lg" 
                    onClick={handleShare}
                    data-testid="action-share-btn"
                  >
                    <Share2 className="w-4 h-4 mr-1.5" />
                    <span className="hidden sm:inline">Share</span>
                  </Button>
                  
                  {/* Bookmark/Save button */}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className={`h-9 w-9 p-0 rounded-lg ${isSaved ? 'text-cyan-400 bg-cyan-400/10' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
                    data-testid="action-save-btn"
                    onClick={() => {
                      if (!token) { toast.error('Sign in to save games'); return; }
                      const method = isSaved ? 'DELETE' : 'POST';
                      fetch(`${API_URL}/api/user/saved-games/${gameId}`, {
                        method,
                        headers: { 'Authorization': `Bearer ${token}` },
                      }).then(r => {
                        if (r.ok) {
                          setIsSaved(!isSaved);
                          toast.success(isSaved ? 'Removed from favorites' : 'Saved to favorites');
                        }
                      }).catch(() => toast.error('Failed to save'));
                    }}
                  >
                    <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                  </Button>

                  {/* Stream Support button */}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-9 px-3 rounded-lg text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    data-testid="action-report-btn"
                    onClick={() => setShowReportModal(true)}
                  >
                    <Flag className="w-4 h-4 mr-1.5" />
                    <span className="hidden sm:inline">Stream Support</span>
                  </Button>
                  
                  {/* Removed redundant fullscreen button - using single button in video controls */}
                </div>
                
                {/* Right: Viewer count - only show for live/replay games */}
                {(game.status === 'live' || game.status === 'halftime' || game.status === 'warmup') && (
                <div className="flex items-center gap-1.5 text-xs text-zinc-500 shrink-0">
                  <Users className="w-3.5 h-3.5" />
                  <span>{game.current_viewers || 0}</span>
                </div>
                )}
              </div>
            )}

            {/* Timestamp Markers List (for live games) */}
            {showMarkersList && timestampMarkers.length > 0 && (
              <div className="mt-3 bg-zinc-900 border border-zinc-700 rounded-lg p-3" data-testid="markers-list">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Flag className="w-4 h-4 text-amber-400" />
                    Your Marked Moments ({timestampMarkers.length})
                  </h4>
                  <button 
                    onClick={() => setShowMarkersList(false)}
                    className="text-zinc-500 hover:text-white text-xs"
                  >
                    Close
                  </button>
                </div>
                <p className="text-xs text-zinc-500 mb-3">
                  These moments are saved. After the game ends, you can create clips from the VOD replay.
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {timestampMarkers.map((marker) => (
                    <div key={marker.id} className="flex items-center justify-between bg-zinc-800 rounded px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-amber-400 font-mono">
                          {Math.floor(marker.timestamp_seconds / 60)}:{String(Math.floor(marker.timestamp_seconds % 60)).padStart(2, '0')}
                        </span>
                        <span className="text-zinc-300 truncate max-w-[200px]">{marker.label}</span>
                        {marker.note && <span className="text-zinc-500 text-xs">({marker.note})</span>}
                      </div>
                      <button 
                        onClick={() => deleteMarker(marker.id)}
                        className="text-zinc-500 hover:text-red-400 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Game Tabs - BallerTV Style */}
            {!isFullscreen && (
              <div className="border-b border-zinc-800 mt-4">
                <div className="flex gap-1">
                  {(() => {
                    const isVOD = game.status === 'final' || game.status === 'ended' || game.status === 'completed' || game.status === 'replay';
                    const tabs = isVOD ? ['Overview', 'Stats', 'Roster', 'Clips'] : ['Overview', 'Stats', 'Roster'];
                    return tabs.map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab.toLowerCase())}
                      className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                        activeTab === tab.toLowerCase()
                          ? 'text-white'
                          : 'text-zinc-500 hover:text-zinc-300'
                      }`}
                      data-testid={`tab-${tab.toLowerCase()}`}
                    >
                      {tab}
                      {activeTab === tab.toLowerCase() && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                      )}
                    </button>
                  ));
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Scoreboard & Info - Always visible */}
          <div className="order-2 lg:row-span-2 space-y-3">
            {/* Compact Teams with scores */}
            <div className="bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800">
              {/* Home Team */}
              <div className="p-3 sm:p-4 border-b border-zinc-800">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div 
                      className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-white font-bold text-sm sm:text-lg flex-shrink-0"
                      style={{ backgroundColor: game.home_team_color || '#3B82F6' }}
                    >
                      {game.home_team_name?.charAt(0) || 'H'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-white text-xs sm:text-base leading-tight truncate">{game.home_team_name}</p>
                      <p className="text-[10px] sm:text-xs text-zinc-500 hidden sm:block">0 followers</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    <div 
                      className="text-2xl sm:text-4xl font-black tabular-nums"
                      style={{ color: game.home_team_color || '#3B82F6' }}
                    >
                      {game.score?.home || 0}
                    </div>
                    <Button 
                      variant="outline" size="sm" 
                      className={`text-[10px] sm:text-sm hidden sm:flex ${followedTeams.has(game.home_team_name) ? 'border-cyan-500 text-cyan-400' : 'border-zinc-600 text-zinc-400 hover:text-white'}`}
                      data-testid="follow-home-team-btn"
                      onClick={() => {
                        if (!token) { toast.error('Sign in to follow teams'); return; }
                        const name = game.home_team_name;
                        const isFollowing = followedTeams.has(name);
                        if (isFollowing) {
                          fetch(`${API_URL}/api/user/followed-teams/${encodeURIComponent(name)}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })
                            .then(() => { setFollowedTeams(prev => { const n = new Set(prev); n.delete(name); return n; }); toast.success(`Unfollowed ${name}`); });
                        } else {
                          fetch(`${API_URL}/api/user/followed-teams`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ team_name: name }) })
                            .then(() => { setFollowedTeams(prev => new Set([...prev, name])); toast.success(`Following ${name}`); });
                        }
                      }}
                    >
                      {followedTeams.has(game.home_team_name) ? <UserMinus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> : <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />}
                      {followedTeams.has(game.home_team_name) ? 'Following' : 'Follow'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Away Team */}
              <div className="p-3 sm:p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div 
                      className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-white font-bold text-sm sm:text-lg flex-shrink-0"
                      style={{ backgroundColor: game.away_team_color || '#EF4444' }}
                    >
                      {game.away_team_name?.charAt(0) || 'A'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-white text-xs sm:text-base leading-tight truncate">{game.away_team_name}</p>
                      <p className="text-[10px] sm:text-xs text-zinc-500 hidden sm:block">0 followers</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    <div 
                      className="text-2xl sm:text-4xl font-black tabular-nums"
                      style={{ color: game.away_team_color || '#EF4444' }}
                    >
                      {game.score?.away || 0}
                    </div>
                    <Button 
                      variant="outline" size="sm" 
                      className={`text-[10px] sm:text-sm hidden sm:flex ${followedTeams.has(game.away_team_name) ? 'border-cyan-500 text-cyan-400' : 'border-zinc-600 text-zinc-400 hover:text-white'}`}
                      data-testid="follow-away-team-btn"
                      onClick={() => {
                        if (!token) { toast.error('Sign in to follow teams'); return; }
                        const name = game.away_team_name;
                        const isFollowing = followedTeams.has(name);
                        if (isFollowing) {
                          fetch(`${API_URL}/api/user/followed-teams/${encodeURIComponent(name)}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })
                            .then(() => { setFollowedTeams(prev => { const n = new Set(prev); n.delete(name); return n; }); toast.success(`Unfollowed ${name}`); });
                        } else {
                          fetch(`${API_URL}/api/user/followed-teams`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ team_name: name }) })
                            .then(() => { setFollowedTeams(prev => new Set([...prev, name])); toast.success(`Following ${name}`); });
                        }
                      }}
                    >
                      {followedTeams.has(game.away_team_name) ? <UserMinus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> : <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />}
                      {followedTeams.has(game.away_team_name) ? 'Following' : 'Follow'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Compact Game Info Card */}
            <div className="bg-zinc-900 rounded-xl p-2.5 sm:p-4 border border-zinc-800">
              {/* Mobile: Single line summary */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant={isLive ? 'default' : 'secondary'} className={`${isLive ? 'bg-red-600' : ''} text-[10px] sm:text-xs`}>
                    {game.status?.toUpperCase()}
                  </Badge>
                  <div className="flex items-center gap-1.5 text-[10px] sm:text-sm text-zinc-400">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="font-mono">{formatTime(game.clock?.time_remaining_ms)}</span>
                    <span className="text-zinc-600">•</span>
                    <span>P{game.clock?.period || 1}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] sm:text-sm text-zinc-400">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate max-w-[120px] sm:max-w-none">{game.venue_name || game.court_name || game.location || 'Venue TBD'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tab-switched content panels */}
          <div className="lg:col-span-2 order-3">
            {/* OVERVIEW tab: Play-by-play feed */}
            {activeTab === 'overview' && (
              <div data-testid="tab-panel-overview">
                <PlayByPlayFeed 
                  gameId={gameId} 
                  homeTeamName={game.home_team_name}
                  awayTeamName={game.away_team_name}
                  homeTeamColor={game.home_team_color}
                  awayTeamColor={game.away_team_color}
                />
              </div>
            )}

            {/* STATS tab: Box score + AI game analytics */}
            {activeTab === 'stats' && (
              <div data-testid="tab-panel-stats">
                <div id="box-score-section">
                  <BoxScoreStats gameId={gameId} gameState={game} />
                </div>
                {(game.status === 'final' || game.status === 'final-approved') && (
                  <div className="mt-4">
                    <GamePlayerStats gameId={gameId} gameState={game} />
                  </div>
                )}
              </div>
            )}

            {/* ROSTER tab: Team rosters */}
            {activeTab === 'roster' && (
              <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 text-center" data-testid="tab-panel-roster">
                <Users className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
                <p className="text-zinc-300 font-semibold mb-1">Team Rosters</p>
                <p className="text-sm text-zinc-500">
                  Full roster details for {game.home_team_name} and {game.away_team_name} are available on each team&apos;s HQ page.
                </p>
              </div>
            )}

            {/* CLIPS tab: Highlight clips from this game */}
            {activeTab === 'clips' && (
              <div data-testid="tab-panel-clips">
                {clipsLoading ? (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-10 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : gameClips.length === 0 ? (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-8 text-center">
                    <Video className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
                    <p className="text-zinc-300 font-semibold mb-1">No clips yet for this game</p>
                    <p className="text-sm text-zinc-500">
                      Use the &quot;Create Clip&quot; button on the video player to save your favorite moments.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {gameClips.map(clip => (
                      <Link
                        key={clip.clip_id}
                        to={`/clip/${clip.clip_id}`}
                        className="group relative block aspect-video rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-zinc-600 transition-all"
                        data-testid={`clip-tile-${clip.clip_id}`}
                      >
                        {clip.thumbnail_url ? (
                          <img
                            src={clip.thumbnail_url}
                            alt={clip.title || 'Clip'}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Video className="w-8 h-8 text-zinc-700" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play className="w-4 h-4 text-black ml-0.5" fill="black" />
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="text-white font-semibold text-sm line-clamp-2">
                            {clip.title || 'Highlight'}
                          </p>
                          {clip.duration_seconds && (
                            <p className="text-[10px] text-zinc-300 mt-0.5">
                              {Math.round(clip.duration_seconds)}s
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Stream Issue Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowReportModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()} data-testid="report-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-base flex items-center gap-2">
                <Flag className="w-4 h-4 text-red-500" />
                Stream Support
              </h3>
              <button onClick={() => setShowReportModal(false)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-zinc-500 mb-4">Select the issue you&apos;re experiencing. Our team will be notified immediately.</p>
            <div className="space-y-2 mb-4">
              {[
                { value: 'stream_not_loading', label: 'Stream not loading' },
                { value: 'audio_issues', label: 'Audio issues (no sound, distorted)' },
                { value: 'video_quality', label: 'Video quality (buffering, pixelated)' },
                { value: 'wrong_game', label: 'Wrong game / camera angle' },
                { value: 'stream_offline', label: 'Stream went offline' },
                { value: 'other', label: 'Other' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setReportType(opt.value)}
                  data-testid={`report-option-${opt.value}`}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all ${
                    reportType === opt.value 
                      ? 'bg-amber-400/15 border border-amber-400/40 text-amber-300' 
                      : 'bg-zinc-800 border border-zinc-700 text-zinc-300 hover:border-zinc-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {reportType === 'other' && (
              <textarea
                value={reportNote}
                onChange={e => setReportNote(e.target.value)}
                placeholder="Describe the issue..."
                data-testid="report-note-input"
                className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg p-3 text-sm resize-none h-20 mb-4 focus:outline-none focus:border-zinc-500"
              />
            )}
            <Button
              onClick={handleReportSubmit}
              disabled={reportSubmitting || !reportType}
              data-testid="report-submit-btn"
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-full"
            >
              {reportSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Flag className="w-4 h-4 mr-2" />}
              {reportSubmitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
